/**
 * LSP-22818: on Android, files picked through the system Photos picker are served to Chrome as
 * stand-in ("synthetic") files. When three or more of them are read at the same time, Android's
 * MediaProvider re-creates the stand-in (`createSparseFile` → `setLength`), its modified time
 * moves, and Chrome fails that read and every later one with ERR_UPLOAD_FILE_CHANGED
 * (`NotReadableError`, or `TypeError: network error` from `stream()`). One or two readers at a time
 * never tripped it on a Galaxy S22 / Chrome 155, so on Android every read of a picked file goes
 * through this gate one at a time. Reads that are already open are not re-checked by Chrome, so
 * only the start of each read needs the gate; a slice read takes milliseconds, so parallel S3 PUTs
 * are unaffected.
 *
 * Limit: one in-page mutex, not a cross-tab lock — a second tab or the contact-async
 * background-upload frame reading its own picks at the same time is not covered.
 */

/** A read that hasn't settled by now (a stuck cloud item) stops holding the gate so the rest of the batch keeps moving. */
export const PICKED_FILE_READ_HOLD_LIMIT_MS = 10_000;

export interface PickedFileReadGateOptions {
	enabled: () => boolean;
	holdLimitMs?: number;
	onFirstGatedRead?: () => void;
	onHoldLimitReached?: (holdLimitMs: number) => void;
}

export type PickedFileReadGate = <T>(read: () => Promise<T>) => Promise<T>;

export function createPickedFileReadGate(options: PickedFileReadGateOptions): PickedFileReadGate {
	const holdLimitMs = options.holdLimitMs ?? PICKED_FILE_READ_HOLD_LIMIT_MS;
	let tail: Promise<void> = Promise.resolve();
	let announced = false;

	return <T>(read: () => Promise<T>): Promise<T> => {
		if (!options.enabled()) return read();
		if (!announced) {
			announced = true;
			options.onFirstGatedRead?.();
		}
		const turn = tail.then(() => {
			const reading = read();
			const released = new Promise<void>((release) => {
				const timer = setTimeout(() => {
					options.onHoldLimitReached?.(holdLimitMs);
					release();
				}, holdLimitMs);
				const settle = () => {
					clearTimeout(timer);
					release();
				};
				reading.then(settle, settle);
			});
			return { reading, released };
		});
		tail = turn.then(
			({ released }) => released,
			() => undefined
		);
		return turn.then(({ reading }) => reading);
	};
}

export function isAndroidBrowser(): boolean {
	const browserNavigator = globalThis.navigator as (Navigator & { userAgentData?: { platform?: string } }) | undefined;
	// userAgentData can still report Android when "Desktop site" swaps the user agent to a Linux desktop one.
	if (browserNavigator?.userAgentData?.platform === "Android") return true;
	const userAgent = browserNavigator?.userAgent;
	return typeof userAgent === "string" && /android/i.test(userAgent);
}

/** Wrap every read of a user-picked File in this: `withPickedFileRead(() => file.slice(a, b).arrayBuffer())`. */
export const withPickedFileRead: PickedFileReadGate = createPickedFileReadGate({
	enabled: isAndroidBrowser,
	onFirstGatedRead: () => console.info("[Upload] Reading picked files one at a time on Android (LSP-22818)"),
	onHoldLimitReached: (holdLimitMs) =>
		console.warn("[Upload] A picked-file read is still pending; letting the next read start (LSP-22818)", { holdLimitMs })
});

/**
 * For a read raced against its own timeout: `start` runs when the read's turn begins (so the timer
 * never counts queueing), the caller gets `result` as soon as it settles, but the gate stays held
 * until `settled` (the underlying read) finishes or hits the hold limit, so a timed-out read that
 * is still open never overlaps the next one.
 */
export function withPickedFileReadTurn<T>(start: () => { result: Promise<T>; settled: Promise<unknown> }): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		withPickedFileRead(() => {
			const turn = start();
			turn.result.then(resolve, reject);
			return turn.settled.then(
				() => undefined,
				() => undefined
			);
		}).catch(reject);
	});
}
